import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "~/utils/helpers"

const CELL = 10
const DOT_RADIUS = 3.4

interface GridSpec {
  dotsPerSide: number
  animation: string
  durationS: number
  crossFraction: number
}

const buildGrid = ({ dotsPerSide, animation, durationS, crossFraction }: GridSpec) => ({
  extent: dotsPerSide * CELL,
  dotClassName: cn(
    "origin-center [transform-box:fill-box] motion-reduce:animate-loader-dot-fade",
    animation,
  ),
  dots: Array.from({ length: dotsPerSide * dotsPerSide }, (_, i) => {
    const x = i % dotsPerSide
    const y = Math.floor(i / dotsPerSide)
    const phase = ((x + y) / (2 * (dotsPerSide - 1))) * crossFraction
    return {
      key: `${x}-${y}`,
      cx: x * CELL + CELL / 2,
      cy: y * CELL + CELL / 2,
      // Negative delay starts every dot mid-cycle, so the wave is already moving on mount.
      style: {
        animationDuration: `${durationS}s`,
        animationDelay: `${((phase - 1) * durationS).toFixed(3)}s`,
      },
    }
  }),
})

const SWEEP = buildGrid({
  dotsPerSide: 5,
  animation: "animate-loader-dot-flip",
  durationS: 1.5,
  crossFraction: 0.55,
})

/**
 * Sizes are roles: `sm` sits inline (buttons, cells, badges), `base` is a standalone loader filling
 * a card, tab, panel or dialog (LoadingOverlay's default), `lg` fills the whole screen.
 */
const loadingVariants = cva("inline-block shrink-0 size-[1em] text-current", {
  variants: {
    size: {
      sm: "text-lg",
      base: "text-[32px]",
      lg: "text-5xl",
    },
  },
  defaultVariants: {
    size: "base",
  },
})

type LoadingSize = NonNullable<VariantProps<typeof loadingVariants>["size"]>

// Inline spinners stay a calm 3×3; a 5×5 at 14px is too busy to read.
const GRID_BY_SIZE: Record<LoadingSize, ReturnType<typeof buildGrid>> = {
  sm: buildGrid({
    dotsPerSide: 3,
    animation: "animate-loader-dot-wave",
    durationS: 1.1,
    crossFraction: 0.5,
  }),
  base: SWEEP,
  lg: SWEEP,
}

interface LoadingProps extends VariantProps<typeof loadingVariants> {
  className?: string
}

function Loading({ size, className }: LoadingProps = {}) {
  const grid = GRID_BY_SIZE[size ?? "base"]

  return (
    <svg
      viewBox={`0 0 ${grid.extent} ${grid.extent}`}
      className={cn(loadingVariants({ size }), className)}
      aria-hidden="true"
    >
      {grid.dots.map(({ key, ...dot }) => (
        <circle
          key={key}
          {...dot}
          r={DOT_RADIUS}
          fill="currentColor"
          className={grid.dotClassName}
        />
      ))}
    </svg>
  )
}

export { Loading, loadingVariants }
